<template>
  <BittsLoading :is-loading="!isReady">
    <div class="api-integrations-tab">
      <ApiTabEmptyState
        v-if="!oAuthApplications.length"
        tab="integrations"
        title="No integrations yet"
        description="Create an integration to get a client ID and secret, then exchange them for tokens using the OAuth 2.0 flow."
        create-cta-text="Create integration"
        :icon="['fak', 'integration']"
        :create-disabled-reason="createDisabledReason"
      />
      <template v-else>
        <ApiIntegrationCard
          v-for="integration in oAuthApplications"
          :key="integration.id"
          :integration="integration"
          :can-manage="canWrite"
          @settings="openDetail('integrations', integration.id)"
          @delete="integrationPendingDelete = integration"
        />
      </template>
      <BittsModal
        v-if="integrationPendingDelete"
        variant="confirm"
        :icon="['fas', 'trash-can']"
        icon-color="danger"
        title="Delete integration?"
        :description="deleteDescription"
        primary-button-text="Delete integration"
        primary-button-type="danger"
        secondary-button-text="Cancel"
        :action-loading="isDeleting"
        @action="handleDelete"
        @closed="integrationPendingDelete = null"
      />
    </div>
  </BittsLoading>
</template>

<script setup lang="ts">
import { BittsLoading, BittsModal } from '@crossbeam/bitts';

import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import { useFlashesStore, useOauthApplicationStore } from '@/stores';
import type { OauthApplication } from '@/types/oauth_applications';

import ApiIntegrationCard from '../components/ApiIntegrationCard.vue';
import ApiTabEmptyState from '../components/ApiTabEmptyState.vue';
import useApiIntegrationCreateGate from '../useApiIntegrationCreateGate';
import useApiRoutes from '../useApiRoutes';

useHead({ title: 'API Integrations - Crossbeam' });

const { openDetail } = useApiRoutes();
const { canWrite, createDisabledReason } = useApiIntegrationCreateGate();
const flashesStore = useFlashesStore();
const oAuthApplicationStore = useOauthApplicationStore();
const { oAuthApplications, ready: isReady } = storeToRefs(
  oAuthApplicationStore,
);

const integrationPendingDelete = ref<OauthApplication | null>(null);
const isDeleting = ref(false);

const deleteDescription = computed(
  () =>
    `"${integrationPendingDelete.value?.name}" will stop working immediately for anything using its client ID and secret. This cannot be undone.`,
);

async function handleDelete() {
  if (!integrationPendingDelete.value) return;
  isDeleting.value = true;
  try {
    await oAuthApplicationStore.deleteOAuthApplication(
      integrationPendingDelete.value.id,
    );
    flashesStore.addSuccessFlash({ message: 'Integration deleted' });
    integrationPendingDelete.value = null;
  } catch {
    flashesStore.addErrorFlash({
      message: 'Something went wrong',
      description: 'Integration not deleted',
    });
    await oAuthApplicationStore.refreshOAuthApplicationsStore();
  } finally {
    isDeleting.value = false;
  }
}
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";

.api-integrations-tab {
  @apply flex flex-col gap-16;
}
</style>
